import { keys, size } from 'lodash';
import { CAMPAIGN_STATUS } from 'modules/campaigns/config';
import { MODULE_NAME as CAMPAIGNS_MODULE } from 'modules/campaigns/config/routes/module';
import { generateBulkCampaignEditorPath } from 'modules/campaigns/services/utils';
import { FEATURE_FLAGS } from 'modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import {
    isAdminNetworkAccountType,
    isNetworkAccountType,
    isProgrammaticAccount,
} from '../../../../../../account-management/accountType';
import { isSponsoredAccountAndDimension } from '../../../../../config/report-predicates/isSponsoredAccountAndDimension';
import { REPORT_PERMISSION } from '../../../../../config/reportPermissions';
import { TOP_RESULTS_TYPE } from '../../../../../constants';
import { updateCampaigns } from '../../../../../flows';
import { BULK_ACTION } from '../../../components/BulkActionToolbar/bulkAction';
import * as FIELDS from '../../fields';
import { FILTER_TYPE } from '../../filters';
import { REPORT_PRESET } from '../../pickers';
import { CAMPAIGN_REPORT_ID } from './campaignReportId';
import columnDefinitions from './columnDefinitions';
import footerColumnDefinitions from './footerColumnDefinitions';
import { graph } from './graph';
import { customColumns, defaultColumns, performanceColumns, setupColumns } from './reportPresets';

const CAMPAIGN_EDIT_LIMIT = 200;

export const campaignReportConfig = {
    id: CAMPAIGN_REPORT_ID,
    label: 'Campaigns',
    accountConfigurations: {
        [FEATURE_FLAGS.HIDE_HEAVY_ACCOUNT_CAMPAIGNS_REPORT]: 'false',
    },
    visibilityCondition: isSponsoredAccountAndDimension,
    module: CAMPAIGNS_MODULE,
    endpoint: 'campaign-report-by-campaign-daily',
    graph,
    rowIdField: FIELDS.ID.field,
    columnsDef: columnDefinitions,
    presets: {
        [REPORT_PRESET.DEFAULT]: { columns: defaultColumns },
        [REPORT_PRESET.PERFORMANCE]: { columns: performanceColumns },
        [REPORT_PRESET.SETUP]: { columns: setupColumns },
    },
    customPreset: { columns: customColumns, initialState: defaultColumns },
    footerColumnDef: footerColumnDefinitions,
    permissions: [REPORT_PERMISSION.ADS_CONSOLE_CAMPAIGNS_REPORT],
    inlineEditPermissions: ['CAMPAIGN_EDIT'],
    searchPlaceholderMsgId: 'app.reports.byCampaign.search.placeholder',

    // TODO: DEV-58584 need to convert filter names in the GW. replace sortField + remove sortMap
    // searchField: NAME.field
    searchField: FIELDS.CAMPAIGN_NAME.field,
    sortMap: {
        [FIELDS.NAME.field]: FIELDS.CAMPAIGN_NAME.field,
        [FIELDS.ID.field]: 'campaignId',
        [FIELDS.STATUS.field]: 'campaignStatus',
        [FIELDS.DEMAND_TYPE.field]: 'type',
    },
    bulkActions: [
        {
            key: BULK_ACTION.PLAY,
            handler:
                ({ accountName, selectedRows }) =>
                dispatch =>
                    dispatch(updateCampaigns(accountName, keys(selectedRows), { isActive: true })),
        },
        {
            key: BULK_ACTION.PAUSE,
            handler:
                ({ accountName, selectedRows }) =>
                dispatch =>
                    dispatch(updateCampaigns(accountName, keys(selectedRows), { isActive: false })),
        },
        {
            key: BULK_ACTION.EDIT,
            handler:
                ({ selectedRows, accountName, navigate }) =>
                () => {
                    const bulkEditCampaignPath = generateBulkCampaignEditorPath({
                        accountId: accountName,
                        formEntityIds: keys(selectedRows),
                    });

                    navigate(bulkEditCampaignPath);
                },
            accountConfigurations: { [FEATURE_FLAGS.CAMPAIGN_BULK_EDIT_ENABLED]: 'true' },
            enableCondition: ({ selectedRows = {}, selectedAccount: { type } }) =>
                !isNetworkAccountType(type) && size(selectedRows) <= CAMPAIGN_EDIT_LIMIT,
            getTooltip: ({ selectedRows = {}, selectedAccount: { type } }) => {
                if (isNetworkAccountType(type)) {
                    return (
                        <FormattedMessage
                            id="campaign.bulk.button.disabled.tooltip.edit.network"
                            defaultMessage="Bulk edit is unavailable at the network level. Please select a single account to use bulk edit."
                        />
                    );
                }
                if (size(selectedRows) > CAMPAIGN_EDIT_LIMIT) {
                    return (
                        <FormattedMessage
                            id="campaign.bulk.button.disabled.tooltip.edit.limit"
                            defaultMessage={`Can only edit ${CAMPAIGN_EDIT_LIMIT} campaigns at a time. Please deselect some before proceeding.`}
                            values={{
                                limit: CAMPAIGN_EDIT_LIMIT,
                            }}
                        />
                    );
                }
            },
        },
    ],
    initialFilters: [
        {
            id: FILTER_TYPE.CAMPAIGN_STATUS,
            type: FILTER_TYPE.CAMPAIGN_STATUS,
            values: [
                { value: CAMPAIGN_STATUS.RECENT, label: 'Recent', messageId: 'app.campaigns.campaign.status.RECENT' },
            ],
            accountConfigurations: { [FEATURE_FLAGS.REPORT_AUTO_FILTERS]: 'true' },
        },
        {
            id: FILTER_TYPE.TOP_RESULTS,
            type: FILTER_TYPE.TOP_RESULTS,
            values: [{ value: TOP_RESULTS_TYPE.TOP100, label: 'Top 100', messageId: 'report.topResults.100' }],
            accountConfigurations: {
                [FEATURE_FLAGS.HEAVY_ACCOUNT]: 'true',
                [FEATURE_FLAGS.CAMPAIGN_REPORT_TOP_RESULTS_FILTER_ACTIVE]: 'true',
            },
        },
    ],
    filters: [
        { id: FILTER_TYPE.CAMPAIGN_STATUS, multiSelect: true, minVisibleCount: 4 },
        {
            id: FILTER_TYPE.TOP_RESULTS,
            accountConfigurations: {
                [FEATURE_FLAGS.CAMPAIGN_REPORT_TOP_RESULTS_FILTER_ACTIVE]: 'true',
            },
        },
        {
            id: FILTER_TYPE.CAMPAIGN_ID,
            multiSelect: true,
            permissions: ['ADS_CONSOLE_ACCOUNTS_FILTERS'],
        },
        {
            id: FILTER_TYPE.PLATFORM,
            accountConfigurations: { [FEATURE_FLAGS.HIDE_HEAVY_ACCOUNT_HIDE_CAMPAIGNS_REPORT_FILTERS]: 'false' },
            visibilityCondition: ({ selectedAccount: { type } }) => !isAdminNetworkAccountType(type),
        },
        {
            id: FILTER_TYPE.COUNTRY,
            accountConfigurations: { [FEATURE_FLAGS.HIDE_HEAVY_ACCOUNT_HIDE_CAMPAIGNS_REPORT_FILTERS]: 'false' },
            visibilityCondition: ({ selectedAccount: { type } }) => !isAdminNetworkAccountType(type),
        },
        {
            id: FILTER_TYPE.SITE,
            accountConfigurations: { [FEATURE_FLAGS.HIDE_HEAVY_ACCOUNT_HIDE_CAMPAIGNS_REPORT_FILTERS]: 'false' },
            visibilityCondition: ({ selectedAccount: { type } }) => !isAdminNetworkAccountType(type),
        },
        {
            id: FILTER_TYPE.OS_FAMILY,
            accountConfigurations: { [FEATURE_FLAGS.CAMPAIGN_REPORT_USE_WIDE_CONFIG_MSV2]: 'true' },
            visibilityCondition: ({ selectedAccount: { type } }) => !isAdminNetworkAccountType(type),
        },
        {
            id: FILTER_TYPE.OS_VERSION,
            accountConfigurations: { [FEATURE_FLAGS.CAMPAIGN_REPORT_USE_WIDE_CONFIG_MSV2]: 'true' },
            visibilityCondition: ({ selectedAccount: { type } }) => !isAdminNetworkAccountType(type),
        },
        {
            id: FILTER_TYPE.BROWSER,
            accountConfigurations: { [FEATURE_FLAGS.CAMPAIGN_REPORT_USE_WIDE_CONFIG_MSV2]: 'true' },
            visibilityCondition: ({ selectedAccount: { type } }) => !isAdminNetworkAccountType(type),
        },
        {
            id: FILTER_TYPE.TARGET_PAGE_TYPE,
            permissions: ['ADS_CONSOLE_ACCOUNTS_FILTERS'],
        },
        {
            id: FILTER_TYPE.CONTENT_SAFETY,
            permissions: ['ADS_CONSOLE_ACCOUNTS_FILTERS'],
        },
        {
            id: FILTER_TYPE.CONTENT_TYPE,
            permissions: ['ADS_CONSOLE_ACCOUNTS_FILTERS'],
        },
        {
            id: FILTER_TYPE.CATEGORY_TYPE,
            permissions: ['ADS_CONSOLE_ACCOUNTS_FILTERS'],
            accountConfigurations: { [FEATURE_FLAGS.SHOULD_DISPLAY_LEGACY_CATEGORY_FILTER]: 'true' },
        },
        {
            id: FILTER_TYPE.IAB_CATEGORY_TYPE,
            permissions: ['ADS_CONSOLE_ACCOUNTS_FILTERS'],
            accountConfigurations: { [FEATURE_FLAGS.SHOULD_DISPLAY_IAB_CATEGORY_FILTER]: 'true' },
        },
        {
            id: FILTER_TYPE.ACCOUNT_MANAGER,
            permissions: ['ADS_CONSOLE_ACCOUNTS_FILTERS', 'INTERNAL_FILTERS'],
        },
        {
            id: FILTER_TYPE.SALES_PERSON,
            permissions: ['ADS_CONSOLE_ACCOUNTS_FILTERS', 'INTERNAL_FILTERS'],
        },
        {
            id: FILTER_TYPE.SALES_GROUP,
            permissions: ['ADS_CONSOLE_ACCOUNTS_FILTERS', 'INTERNAL_FILTERS'],
        },
        {
            id: FILTER_TYPE.ACCOUNT_MANAGER_GROUP,
            permissions: ['ADS_CONSOLE_ACCOUNTS_FILTERS', 'INTERNAL_FILTERS'],
        },
        {
            id: FILTER_TYPE.LANGUAGE,
            permissions: ['ADS_CONSOLE_ACCOUNTS_FILTERS', 'INTERNAL_FILTERS'],
        },
        {
            id: FILTER_TYPE.DEMAND_TYPE,
            permissions: ['ADS_CONSOLE_ACCOUNTS_FILTERS'],
            visibilityCondition: ({ selectedAccount }) => isProgrammaticAccount(selectedAccount),
        },
        { id: FILTER_TYPE.BID_STRATEGY },
    ],
};

export default campaignReportConfig;
